import { escapeHtml } from '@/common/utils/strings'
import { AIAssistantSelectedDocument } from '@/types/ai-assistant'

export const NEW_PROMPT_MAX_LENGTH = 3000

// ASK_DOC requires at least ASK_DOC_MIN_DOCUMENTS selected documents (full-room search removed).
export const ASK_DOC_MIN_DOCUMENTS = 1
// Default max documents used until the backend value (conversations endpoint) is loaded. -1 = no limit.
export const ASK_DOC_MAX_DOCUMENTS = 5

// Whether the user can submit a prompt. For ASK_DOC, at least one document is required,
// and no more than maxDocuments when a positive limit is set (maxDocuments <= 0 means no upper limit).
export function canPostPrompt ({
  disabled,
  prompt,
  maxLength = NEW_PROMPT_MAX_LENGTH,
  isAskDoc = false,
  selectedDocumentsCount = 0,
  maxDocuments = ASK_DOC_MAX_DOCUMENTS,
}: {
  disabled: boolean
  prompt: string
  maxLength?: number
  isAskDoc?: boolean
  selectedDocumentsCount?: number
  maxDocuments?: number
}): boolean {
  if (disabled) return false
  const length = escapeHtml(prompt).length
  if (length === 0) return false
  if (length > maxLength) return false
  if (isAskDoc) {
    if (selectedDocumentsCount < ASK_DOC_MIN_DOCUMENTS) return false
    if (maxDocuments > 0 && selectedDocumentsCount > maxDocuments) return false
  }
  return true
}

/**
 * A selected document whose underlying file has been deleted from the room comes back from
 * the conversation history without an id. It stays visible as a disabled source but can no
 * longer be sent to the back-end.
 */
export function isDeletedDocument (document?: AIAssistantSelectedDocument | null): boolean {
  return document?.id == null
}

/**
 * Keeps only the documents that still exist in the room.
 */
export function liveDocuments (
  documents?: AIAssistantSelectedDocument[] | null,
): Array<AIAssistantSelectedDocument & { id: number }> {
  return (documents || []).filter(
    (document): document is AIAssistantSelectedDocument & { id: number } => document.id != null,
  )
}

/**
 * File ids to send with a prompt. Deleted documents are left out: the back-end detaches the
 * sources missing from the payload, so they stop appearing in the conversation after a reload.
 */
export function sendableFileIds (documents?: AIAssistantSelectedDocument[] | null): number[] {
  return liveDocuments(documents).map(document => document.id)
}

/**
 * Stable `v-for` key for a selected document. Deleted documents all share a null id, so they
 * fall back to their position in the list to stay unique.
 */
export function documentKey (document: AIAssistantSelectedDocument, index: number): string {
  return isDeletedDocument(document)
    ? `${document?.type}-deleted-${index}`
    : `${document.type}-${document.id}`
}
